/**
 * Graph-IDF Sync Service
 *
 * Core service for converting between HVAC graph nodes and IDF objects.
 * Handles bidirectional synchronization logic.
 */

import type { Node, Edge } from 'reactflow';
import type { IDFObject, IDFField } from '../../parser';
import type { HVACNodeData, HVACEdgeData } from '../types';
import type { SyncResult, EdgeSyncData, NodeSyncMetadata, ObjectSyncMetadata } from './types';
import { getComponentMapping, getMappingByIDFClass, isHVACClass } from './fieldMappings';
import { getComponentDefinition } from '../components';

/**
 * Generate a unique ID for IDF objects created from graph nodes
 */
function generateIDFObjectId(): string {
  return `obj_${String(Date.now())}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Generate a node name for EnergyPlus connections
 */
export function generateConnectionNodeName(
  sourceName: string,
  targetName: string,
  sourcePort: string,
  targetPort: string
): string {
  // Clean up names for EnergyPlus compatibility (no special chars, reasonable length)
  const cleanSource = sourceName.replace(/[^a-zA-Z0-9 ]/g, '').substring(0, 30);
  const cleanTarget = targetName.replace(/[^a-zA-Z0-9 ]/g, '').substring(0, 30);
  return `${cleanSource} ${sourcePort} to ${cleanTarget} ${targetPort}`;
}

/**
 * Create an IDF field with optional comment
 */
function createIDFField(value: string, comment?: string): IDFField {
  const field: IDFField = { value };
  if (comment !== undefined) {
    field.inlineComment = comment;
  }
  return field;
}

/**
 * Convert a graph node to an IDF object
 */
export function nodeToIDFObject(
  node: Node<HVACNodeData>,
  portNodeNames: Map<string, string> // portId -> node name
): SyncResult {
  const { componentType, name, fields } = node.data;

  // Get the mapping for this component type
  const mapping = getComponentMapping(componentType);
  if (!mapping) {
    return {
      success: false,
      error: `No IDF mapping found for component type: ${componentType}`,
    };
  }

  // Get component definition for port info
  const definition = getComponentDefinition(componentType);
  if (!definition) {
    return {
      success: false,
      error: `No component definition found: ${componentType}`,
    };
  }

  // Create IDF fields array
  const idfFields: IDFField[] = [];

  // Fill fields up to totalFields with empty values
  for (let i = 0; i < mapping.totalFields; i++) {
    idfFields.push(createIDFField(''));
  }

  // Set the name field
  idfFields[mapping.nameFieldIndex] = createIDFField(name, 'Name');

  // Set field mappings from node fields
  for (const [fieldName, fieldMapping] of Object.entries(mapping.fieldMappings)) {
    const value = fields[fieldName] ?? fieldMapping.defaultValue ?? '';
    if (idfFields[fieldMapping.fieldIndex]) {
      idfFields[fieldMapping.fieldIndex] = createIDFField(value, fieldName);
    }
  }

  // Set port node names from connections
  for (const portMapping of mapping.portMappings) {
    const nodeName = portNodeNames.get(portMapping.portId) || '';
    if (idfFields[portMapping.fieldIndex]) {
      idfFields[portMapping.fieldIndex] = createIDFField(nodeName, portMapping.fieldName);
    }
  }

  // Create the IDF object
  const idfObject: IDFObject = {
    id: generateIDFObjectId(),
    className: mapping.idfClass,
    fields: idfFields,
    leadingComments: [],
  };

  return {
    success: true,
    idfObjectId: idfObject.id,
  };
}

/**
 * Create a complete IDF object from a graph node (returns the actual object)
 */
export function createIDFObjectFromNode(
  node: Node<HVACNodeData>,
  portNodeNames: Map<string, string>,
  existingId?: string
): IDFObject | null {
  const { componentType, name, fields } = node.data;

  // Get the mapping for this component type
  const mapping = getComponentMapping(componentType);
  if (!mapping) {
    console.error(`No IDF mapping found for component type: ${componentType}`);
    return null;
  }

  // Create IDF fields array
  const idfFields: IDFField[] = [];

  // Fill fields up to totalFields with empty values
  for (let i = 0; i < mapping.totalFields; i++) {
    idfFields.push(createIDFField(''));
  }

  // Set the name field
  idfFields[mapping.nameFieldIndex] = createIDFField(name, 'Name');

  // Set field mappings from node fields
  for (const [fieldName, fieldMapping] of Object.entries(mapping.fieldMappings)) {
    const value = fields[fieldName] ?? fieldMapping.defaultValue ?? '';
    if (idfFields[fieldMapping.fieldIndex]) {
      idfFields[fieldMapping.fieldIndex] = createIDFField(value, fieldName);
    }
  }

  // Set port node names from connections
  for (const portMapping of mapping.portMappings) {
    const nodeName = portNodeNames.get(portMapping.portId) || '';
    if (idfFields[portMapping.fieldIndex]) {
      idfFields[portMapping.fieldIndex] = createIDFField(nodeName, portMapping.fieldName);
    }
  }

  // Create the IDF object
  return {
    id: existingId || generateIDFObjectId(),
    className: mapping.idfClass,
    fields: idfFields,
    leadingComments: [],
  };
}

/**
 * Convert an IDF object to graph node data
 */
export function idfObjectToNodeData(idfObject: IDFObject): {
  componentType: string;
  name: string;
  fields: Record<string, string>;
} | null {
  // Check if this IDF class maps to an HVAC component
  if (!isHVACClass(idfObject.className)) {
    return null;
  }

  const mapping = getMappingByIDFClass(idfObject.className);
  if (!mapping) {
    return null;
  }

  // Get component definition
  const definition = getComponentDefinition(mapping.componentType);
  if (!definition) {
    return null;
  }

  // Extract name from IDF object
  const nameField = idfObject.fields[mapping.nameFieldIndex];
  const name = nameField?.value || `${definition.displayName} (imported)`;

  // Extract fields from IDF object
  const fields: Record<string, string> = { ...definition.defaults };

  for (const [fieldName, fieldMapping] of Object.entries(mapping.fieldMappings)) {
    const idfField = idfObject.fields[fieldMapping.fieldIndex];
    if (idfField && idfField.value) {
      fields[fieldName] = idfField.value;
    }
  }

  return {
    componentType: mapping.componentType,
    name,
    fields,
  };
}

/**
 * Extract port node names from an IDF object
 */
export function extractPortNodeNames(idfObject: IDFObject): Map<string, string> {
  const portNodeNames = new Map<string, string>();

  const mapping = getMappingByIDFClass(idfObject.className);
  if (!mapping) {
    return portNodeNames;
  }

  for (const portMapping of mapping.portMappings) {
    const field = idfObject.fields[portMapping.fieldIndex];
    if (field && field.value) {
      portNodeNames.set(portMapping.portId, field.value);
    }
  }

  return portNodeNames;
}

/**
 * Update an existing IDF object with changes from a graph node
 */
export function updateIDFObjectFromNode(
  existingObject: IDFObject,
  node: Node<HVACNodeData>,
  portNodeNames: Map<string, string>
): IDFObject {
  const { componentType, name, fields } = node.data;

  const mapping = getComponentMapping(componentType);
  if (!mapping) {
    console.warn(`No mapping for component type: ${componentType}`);
    return existingObject;
  }

  // Clone the fields
  const newFields = existingObject.fields.map((f) => ({ ...f }));

  // Ensure we have enough fields
  while (newFields.length < mapping.totalFields) {
    newFields.push(createIDFField(''));
  }

  // Update name field
  if (newFields[mapping.nameFieldIndex]) {
    newFields[mapping.nameFieldIndex] = {
      ...newFields[mapping.nameFieldIndex],
      value: name,
    };
  }

  // Update field mappings
  for (const [fieldName, fieldMapping] of Object.entries(mapping.fieldMappings)) {
    const value = fields[fieldName] ?? fieldMapping.defaultValue ?? '';
    if (newFields[fieldMapping.fieldIndex]) {
      newFields[fieldMapping.fieldIndex] = {
        ...newFields[fieldMapping.fieldIndex],
        value,
      };
    }
  }

  // Update port node names
  for (const portMapping of mapping.portMappings) {
    const nodeName = portNodeNames.get(portMapping.portId) || '';
    if (newFields[portMapping.fieldIndex]) {
      newFields[portMapping.fieldIndex] = {
        ...newFields[portMapping.fieldIndex],
        value: nodeName,
      };
    }
  }

  return {
    ...existingObject,
    fields: newFields,
  };
}

/**
 * Calculate port node names from graph edges
 */
export function calculatePortNodeNames(
  nodeId: string,
  edges: Edge<HVACEdgeData>[],
  nodes: Node<HVACNodeData>[]
): Map<string, string> {
  const portNodeNames = new Map<string, string>();

  for (const edge of edges) {
    if (!edge.data) continue;

    const sourceNode = nodes.find((n) => n.id === edge.source);
    const targetNode = nodes.find((n) => n.id === edge.target);

    if (!sourceNode || !targetNode) continue;

    // If this node is the source, set the outlet port node name
    if (edge.source === nodeId && edge.sourceHandle) {
      portNodeNames.set(edge.sourceHandle, edge.data.nodeName);
    }

    // If this node is the target, set the inlet port node name
    if (edge.target === nodeId && edge.targetHandle) {
      portNodeNames.set(edge.targetHandle, edge.data.nodeName);
    }
  }

  return portNodeNames;
}

/**
 * Generate edge sync data from a graph edge
 */
export function createEdgeSyncData(
  edge: Edge<HVACEdgeData>,
  _sourceNode: Node<HVACNodeData>,
  _targetNode: Node<HVACNodeData>,
  sourceObjectId: string,
  targetObjectId: string
): EdgeSyncData | null {
  if (!edge.data || !edge.sourceHandle || !edge.targetHandle) {
    return null;
  }

  return {
    edgeId: edge.id,
    nodeName: edge.data.nodeName,
    sourceObjectId,
    targetObjectId,
    sourcePort: edge.sourceHandle,
    targetPort: edge.targetHandle,
    medium: edge.data.medium,
  };
}

/**
 * Sync node metadata helper
 */
export function createNodeSyncMetadata(
  idfObjectId: string,
  createdFromGraph: boolean
): NodeSyncMetadata {
  return {
    idfObjectId,
    lastSyncTime: Date.now(),
    createdFromGraph,
  };
}

/**
 * Object sync metadata helper
 */
export function createObjectSyncMetadata(
  nodeId: string,
  createdFromIDF: boolean
): ObjectSyncMetadata {
  return {
    nodeId,
    lastSyncTime: Date.now(),
    createdFromIDF,
  };
}

/**
 * Check if a node has changed since last sync
 */
export function hasNodeChanged(
  node: Node<HVACNodeData>,
  lastSyncHash?: string
): boolean {
  if (!lastSyncHash) return true;

  const currentHash = hashNodeData(node.data);
  return currentHash !== lastSyncHash;
}

/**
 * Generate a simple hash of node data for change detection
 */
export function hashNodeData(data: HVACNodeData): string {
  const content = JSON.stringify({
    name: data.name,
    fields: data.fields,
  });
  // Simple hash function (djb2)
  let hash = 5381;
  for (let i = 0; i < content.length; i++) {
    hash = (hash * 33) ^ content.charCodeAt(i);
  }
  return (hash >>> 0).toString(16);
}

/**
 * Generate a simple hash of IDF object data for change detection
 */
export function hashObjectData(object: IDFObject): string {
  const content = JSON.stringify({
    className: object.className,
    fields: object.fields.map((f) => f.value),
  });
  // Simple hash function (djb2)
  let hash = 5381;
  for (let i = 0; i < content.length; i++) {
    hash = (hash * 33) ^ content.charCodeAt(i);
  }
  return (hash >>> 0).toString(16);
}

/**
 * Find IDF objects that match HVAC components
 */
export function findHVACObjects(objects: IDFObject[]): IDFObject[] {
  return objects.filter((obj) => isHVACClass(obj.className));
}

/**
 * Match IDF objects to graph nodes by name
 */
export function matchObjectsToNodes(
  objects: IDFObject[],
  nodes: Node<HVACNodeData>[]
): Map<string, string> {
  const matches = new Map<string, string>(); // objectId -> nodeId

  for (const obj of objects) {
    const mapping = getMappingByIDFClass(obj.className);
    if (!mapping) continue;

    // Get the object's name
    const nameField = obj.fields[mapping.nameFieldIndex];
    if (!nameField || !nameField.value) continue;

    // Find matching node by name and component type
    const matchingNode = nodes.find((node) => {
      return (
        node.data.componentType === mapping.componentType &&
        node.data.name === nameField.value
      );
    });

    if (matchingNode) {
      matches.set(obj.id, matchingNode.id);
    }
  }

  return matches;
}
